import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Typography,
  Divider,
  Badge,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import AuthService from '../../../services/AuthService';

interface AdminHeaderProps {
  onMenuToggle: () => void;
  sidebarOpen?: boolean;
  userName?: string;
  userAvatar?: string;
}

export const AdminHeader: React.FC<AdminHeaderProps> = ({
  onMenuToggle,
  sidebarOpen = true,
  userName: userNameProp,
  userAvatar,
}) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  // Get current user info from AuthService
  const currentUser = AuthService.getUser();
  const userName = userNameProp || currentUser?.name || 'Admin User';
  const userInitial = userName.charAt(0).toUpperCase();

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleMenuClose();
    try {
      // Call logout API to invalidate tokens on server
      await AuthService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear tokens and redirect to login regardless of API response
      AuthService.clearTokens();
      navigate('/admin/login', { replace: true });
    }
  };

  const handleSettings = () => {
    handleMenuClose();
    // TODO: Navigate to settings page
    console.log('Navigate to settings');
  };

  return (
    <Box
      sx={{
        backgroundColor: '#ffffff',
        borderBottom: '1px solid #E6E7EB',
        padding: '0 24px',
        height: '64px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexShrink: 0,
      }}
    >
      {/* Left section */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <IconButton
          onClick={onMenuToggle}
          sx={{
            color: '#5f6368',
            '&:hover': { backgroundColor: '#f5f5f5' },
          }}
          size="small"
        >
          <MenuIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      {/* Right section */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <IconButton
          sx={{
            color: '#5f6368',
            '&:hover': { backgroundColor: '#f5f5f5' },
          }}
          size="small"
        >
          <Badge badgeContent={2} color="error">
            <NotificationsIcon sx={{ fontSize: 18 }} />
          </Badge>
        </IconButton>

        <IconButton
          sx={{
            color: '#5f6368',
            '&:hover': { backgroundColor: '#f5f5f5' },
          }}
          size="small"
        >
          <SettingsIcon sx={{ fontSize: 18 }} />
        </IconButton>

        <Box sx={{ width: '1px', height: '24px', backgroundColor: '#E6E7EB', mx: 0.5 }} />

        <IconButton
          onClick={handleMenuOpen}
          sx={{
            p: 0,
            '&:hover': { backgroundColor: 'transparent' },
          }}
        >
          <Avatar
            sx={{
              width: 32,
              height: 32,
              background: 'linear-gradient(135deg, #6366F1, #8B5CF6)',
              fontSize: '12px',
              fontWeight: 700,
              cursor: 'pointer',
            }}
            src={userAvatar}
          >
            {userInitial}
          </Avatar>
        </IconButton>
      </Box>

      {/* User Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              marginTop: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
              minWidth: '200px',
            },
          },
        }}
      >
        <MenuItem disabled sx={{ opacity: 1, py: 1.5 }}>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500, color: '#333333' }}>
              {userName}
            </Typography>
            <Typography variant="caption" sx={{ color: '#666666' }}>
              {currentUser?.role === 'superadmin' ? 'Super Administrator' : 'Administrator'}
            </Typography>
          </Box>
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem onClick={handleSettings} sx={{ gap: 1.5 }}>
          <SettingsIcon sx={{ fontSize: '18px', color: '#5f6368' }} />
          <Typography variant="body2" sx={{ color: '#333333' }}>
            Cài đặt
          </Typography>
        </MenuItem>
        <MenuItem onClick={handleLogout} sx={{ gap: 1.5 }}>
          <LogoutIcon sx={{ fontSize: '18px', color: '#D32F2F' }} />
          <Typography variant="body2" sx={{ color: '#D32F2F', fontWeight: 500 }}>
            Đăng xuất
          </Typography>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default AdminHeader;
